import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PantryResult {
  id:               string
  name:             string
  emoji:            string
  display_category: string
}

interface Props {
  householdId: string
  listId:      string
  onClose:     () => void
  onAdded:     (itemName: string) => void
}

// ─── Highlight matching text ──────────────────────────────────────────────────

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[#FDF0E3] text-[#D4863A] rounded-sm px-0.5 not-italic">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  )
}

// ─── QuickAddSheet ────────────────────────────────────────────────────────────

export default function QuickAddSheet({ householdId, listId, onClose, onAdded }: Props) {
  const queryClient = useQueryClient()
  const inputRef    = useRef<HTMLInputElement>(null)

  const [query,    setQuery]    = useState('')
  const [results,  setResults]  = useState<PantryResult[]>([])
  const [adding,   setAdding]   = useState(false)

  // Auto-focus search input when sheet opens
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 120)
    return () => clearTimeout(t)
  }, [])

  // Debounced pantry search
  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('pantry_items')
        .select('id, name, emoji, display_category')
        .eq('household_id', householdId)
        .eq('is_active', true)
        .ilike('name', `%${query.trim()}%`)
        .order('name')
        .limit(8)
      setResults((data ?? []) as PantryResult[])
    }, 180)
    return () => clearTimeout(timer)
  }, [query, householdId])

  const hasExactMatch = results.some(
    r => r.name.toLowerCase() === query.trim().toLowerCase()
  )

  // ── Add existing pantry item to list ──────────────────────────────────────

  async function handleSelectItem(item: PantryResult) {
    setAdding(true)
    const { error } = await supabase.from('list_items').insert({
      list_id:       listId,
      item_id:       item.id,
      status:        'pending',
      urgency_score: 0,
      sort_order:    9999,
      is_one_off:    true,
    })
    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] })
      onAdded(item.name)
    }
    setAdding(false)
    onClose()
  }

  // ── Create brand-new pantry item and add to list ──────────────────────────

  async function handleCreateNew() {
    const name = query.trim()
    if (!name) return
    setAdding(true)

    const { data: newItem, error: createErr } = await supabase
      .from('pantry_items')
      .insert({
        household_id:          householdId,
        name,
        emoji:                 '🛒',
        display_category:      'pantry',
        freshness_category:    'pantry_staple',
        
        confirmation_count:    0,
        confidence_score:      0,
        is_active:             true,
      })
      .select()
      .single()

    if (createErr || !newItem) { setAdding(false); return }

    const { error: listErr } = await supabase.from('list_items').insert({
      list_id:       listId,
      item_id:       newItem.id,
      status:        'pending',
      urgency_score: 0,
      sort_order:    9999,
      is_one_off:    true,
    })

    if (!listErr) {
      queryClient.invalidateQueries({ queryKey: ['listItems', listId] })
      onAdded(name)
    }
    setAdding(false)
    onClose()
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Backdrop — z-40, sits above nav (z-30) but below sheet */}
      <motion.div
        className="fixed inset-0 z-40 bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
      />

      {/* Sheet — z-50, above both nav and backdrop
          bottom-16 (64px) lifts it clear of the nav bar */}
      <motion.div
        className="fixed bottom-16 left-0 right-0 z-50 flex justify-center items-end"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
      >
      <div
        className="bg-[#FAF8F4] rounded-t-3xl shadow-2xl w-full max-w-[480px]
                   flex flex-col overflow-hidden"
        style={{ maxHeight: '70vh' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2 shrink-0">
          <div className="w-10 h-1 rounded-full bg-[#E8E2DC]" />
        </div>

        {/* Search input */}
        <div className="px-4 pb-3 shrink-0">
          <div className="flex items-center gap-2 bg-white border border-[#E8E2DC]
                          rounded-xl px-4 py-3 focus-within:border-[#6B9E78] transition-colors">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[#9A9491] shrink-0">
              <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Add something you grabbed..."
              className="flex-1 text-sm text-[#2C2A26] bg-transparent outline-none placeholder-[#C5BFB8]"
            />
            {query && (
              <button onClick={() => setQuery('')} className="text-[#9A9491] text-xs">✕</button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-4 pb-6">
          {results.map(item => (
            <button
              key={item.id}
              onClick={() => handleSelectItem(item)}
              disabled={adding}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl
                         hover:bg-[#F2EDE6] active:bg-[#EDE7DF] transition-colors text-left"
              style={{ minHeight: 48 }}
            >
              <span className="text-xl leading-none shrink-0">{item.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#2C2A26]">
                  <Highlight text={item.name} query={query} />
                </p>
                <p className="text-xs text-[#9A9491] capitalize mt-0.5">
                  {item.display_category.replace(/_/g, ' ')}
                </p>
              </div>
            </button>
          ))}

          {/* "Add as new item" option — shown when typing but no exact match */}
          {query.trim() && !hasExactMatch && (
            <button
              onClick={handleCreateNew}
              disabled={adding}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl
                         hover:bg-[#E8F2EA] active:bg-[#daeee0] transition-colors text-left mt-1"
              style={{ minHeight: 48 }}
            >
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center
                           text-white text-base shrink-0"
                style={{ background: '#6B9E78' }}
              >
                +
              </span>
              <p className="text-sm text-[#2C2A26]">
                Add <span className="font-semibold">"{query.trim()}"</span> as new item
              </p>
            </button>
          )}

          {/* Empty state — no query yet */}
          {!query.trim() && (
            <p className="text-xs text-[#9A9491] text-center pt-4">
              Search your pantry or add something new
            </p>
          )}
        </div>
      </div>
      </motion.div>
    </>
  )
}
